/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Grid, Stack } from "@mui/material";
import FormField from "components/generals/FormInput";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikProps } from "formik";
import { HeaderEnum, TemplateContentTypeEnum, TemplateInterface } from "medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { FormProps, TagsInterface, OptionColor } from "interfaces";
import BadgeIcon from "@mui/icons-material/Badge";
import { H4 } from "components/generals/_Titles";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { CoreAPI_URLs } from "constants/http.const";
import { Method } from "axios";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import FormTextEditor from "components/generals/FormTextEditor";
import Box from "components/generals/Box";
import FormSelectInput from "components/generals/FormSelectInput";
import { templateTypeOptions } from "./custom/SelectOptionsList";
import FormActionButtons from "./custom/FormActionButtons";
import PermissionWrapper from "components/generals/PermissionWrapper";
import AutocompleteCheckboxes from "components/generals/AutocompleteCheckboxes";
import { useEffect, useState } from "react";
import { QueryResultInterface } from "medicalsystem-shared";
import { processTemplateTags, handleAddNewTag } from "utils/template.utils";

/**
 * Form component for creating and managing History of Present Illness (HPI) templates.
 * Provides text editor for template content, tag management, and template type selection.
 * Supports template filtering by tags and validation.
 * @param {FormProps<TemplateInterface>} props - Component props including model, action, lists, and filters
 * @returns {React.ReactElement} The HPI template form component
 */
const HpiTemplateForm: React.FC<FormProps<TemplateInterface>> = ({
  model,
  action,
  onCloseAction,
  readOnlyValue,
  setModelList,
  modelList,
  filterTag,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [optionColors, setOptionColors] = useState<OptionColor[]>([]);
  const [availableTags, setAvailableTags] = useState<TagsInterface[]>([]);
  const [selectedTags, setSelectedTags] = useState<TagsInterface[]>([]);
  const { t } = useTranslation();
  const [newTag, setNewTag] = useState("");

  const onGetHpiHandler = (hpiResponse: QueryResultInterface<TemplateInterface>) => {
    const { availableTags, selectedTags } = processTemplateTags(hpiResponse, filterTag);
    setAvailableTags(availableTags);
    setSelectedTags(selectedTags);
  };

  const handleSelectChange = (tags: TagsInterface[]) => {
    setSelectedTags(tags);
  };

  useEffect(() => {
    sendRequest(CoreAPI_URLs.HPI_TEMPLATE_GET_ALL, onGetHpiHandler, "GET");
    if (filterTag && filterTag.length > 0) {
      setSelectedTags(filterTag);
    }
  }, []);

  const submitHandler = (model: Partial<TemplateInterface>) => {
    model.version = "1.0";
    model.contentType = TemplateContentTypeEnum.TEXT_HTML;
    if (model.tags.length == 0 && filterTag && filterTag.length > 0) {
      model.tags = [...filterTag.map((tag) => tag.name)];
    }
    const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
    sendRequest(
      CoreAPI_URLs.HPI_TEMPLATE_GENERAL_OPERATIONS,
      onHpiTemplateUpdateHandler,
      method,
      JSON.stringify(model),
      [{ header: HeaderEnum.RESOURCE_ID, value: model.id }]
    );
  };

  const handleAddTag = () => {
    const { updatedAvailableTags, updatedSelectedTags } = handleAddNewTag(
      newTag,
      availableTags,
      selectedTags
    );
    setAvailableTags(updatedAvailableTags);
    setSelectedTags(updatedSelectedTags);
    setNewTag("");
    return updatedSelectedTags;
  };

  const onHpiTemplateUpdateHandler = (hpitemplate: TemplateInterface) => {
    if (action === FormActionType.MODIFY) {
      setModelList(modelList.map((model) => (model.id === hpitemplate.id ? hpitemplate : model)));
    } else {
      setModelList((oldArray) => [...oldArray, hpitemplate]);
    }

    onCloseAction();
  };

  const translatedTemplateTypeOptions = templateTypeOptions.map((option) => ({
    ...option,
    label: t(`templateForm.${option.value.toLowerCase()}`), 
  }));

  return (
    <FormikForm
      onSubmit={(model) => {
        submitHandler(model);
      }}
      initialValues={model}
    >
      {({ values, errors, touched, setFieldValue }: FormikProps<Partial<TemplateInterface>>) => (
        <Form id="hpitemplateForm" autoComplete="true">
          <Stack alignItems="center" direction="row" gap={2} mb={3}>
            <BadgeIcon fontSize="large" />
            <H4>{t("hpitemplateForm.hpitemplateData")}</H4>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormField
                readOnly={action === FormActionType.NEW ? false : readOnlyValue}
                type="text"
                label={t("hpitemplateForm.title")}
                name="title"
                value={values.title || ""}
                error={errors.title && touched.title}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextEditor
                readOnly={action === FormActionType.NEW ? false : readOnlyValue}
                setFieldValue={(val) => setFieldValue("template", val)}
                value={values.template}
                editorHeight="30rem"
                wrapperHeight="40rem"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container direction="row" spacing={2} mb={2} alignItems="center">                
                <Grid item xs={9}>
                  <FormField
                    readOnly={action === FormActionType.NEW ? false : readOnlyValue}
                    type="text"
                    label={t("hpitemplateForm.tags")}
                    name="newTag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                  />
                </Grid>
                <Grid item>
                  {(action === FormActionType.NEW || !readOnlyValue) && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => {
                        const updatedSelection = handleAddTag();
                        setFieldValue(
                          "tags",
                          updatedSelection.map((option) => option.name)
                        );
                      }}
                      size="small"
                    >
                      {t("hpitemplateForm.addTag")}
                    </Button>
                  )}
                </Grid>
              </Grid>
              {(action === FormActionType.NEW || !readOnlyValue) && (
                <AutocompleteCheckboxes
                  options={availableTags}
                  label={t("hpitemplateForm.selectTags")}
                  placeholder={t("hpitemplateForm.chooseTags")}
                  onChange={(selected) => {
                    handleSelectChange(selected);
                    setFieldValue(
                      "tags",
                      selected.map((option) => option.name)
                    );
                  }}
                  value={selectedTags}
                  optionColors={optionColors}
                  setOptionColors={setOptionColors}
                  getOptionLabel={(option) => option.name}
                />
              )}
            </Grid>
            <Grid item xs={6} >
              <Box>
                <FormField
                  readOnly={true}
                  type="text"
                  label={t("prescriptionTemplateForm.type")}
                  name="type"
                  value={t("prescriptionTemplateForm.hpi")}
                />
              </Box>
            </Grid>
          </Grid>
          <Stack alignItems="center" direction="row" gap={1}>
            {readOnlyValue === false && (
              <div>
                {action === FormActionType.NEW ? (
                  <PermissionWrapper
                    requiredPermissions={[PermissionsUser[EntityPermissions.HPI_PAGE].CREATE]}
                  >
                    <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                      {t("stepperFormActionButtons.submit")}
                    </Button>
                  </PermissionWrapper>
                ) : (
                  <PermissionWrapper
                    requiredPermissions={[PermissionsUser[EntityPermissions.HPI_PAGE].EDIT]}
                  >
                    <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                      {t("stepperFormActionButtons.update")}
                    </Button>
                  </PermissionWrapper>
                )}
              </div>
            )}

            {readOnlyValue === true && (
              <div>
                {action === FormActionType.NEW ? (
                  <PermissionWrapper
                    requiredPermissions={[PermissionsUser[EntityPermissions.HPI_PAGE].CREATE]}
                  >
                    <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                      {t("stepperFormActionButtons.submit")}
                    </Button>
                  </PermissionWrapper>
                ) : null}
              </div>
            )}

            <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
          </Stack>
        </Form>
      )}
    </FormikForm>
  );
};

export default HpiTemplateForm;
